import React from 'react'
import Hero from 'react-lazy-hero'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import Paper from '@material-ui/core/Paper'
import Fab from '@material-ui/core/Fab'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import MyDatePicker from '../components/MyDatePicker'
import Flip from 'react-reveal/Flip'
import Zoom from 'react-reveal/Zoom'
import ProductLinePicker from '../components/ProductLinePicker'
import classNames from 'classnames'
import { dateTimeFns, dateFns, instantFns } from 'iso-fns2'
import { validateDateFormat } from '../helpers/date-operations'
import WaiverSteps from '../components/waiver-steps-2'
import queryParams from '../helpers/query-params'
import getCountryCode from '../helpers/country-code'
import api from '../api'
import _ from 'lodash'
import * as Sentry from '@sentry/react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import { withTranslation } from 'react-i18next'
import i18n from 'i18next'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { Failed } from '../components/waiver-steps-2/07-failed'
import waiverDataModel, { waiverDataModelUpdateWithProductLine } from '../helpers/step-builder/waiver-web-model'
import uuidv4 from 'uuid/v4'
import { GetExternalUrlsFromDomain } from 'polaris-adventures-js'

/** @typedef {import('../api/WaiverContentDto').WaiverIntegrationContentDto} WaiverIntegrationContentDto */

/** @typedef {import('../api/CustomerWaiverIntegrationDto').CustomerWaiverIntegrationDto} CustomerWaiverIntegrationDto */

const signedWaiverWithDLScan = 'SignedWebWaivers'

class OperatorLanding extends React.Component {
  constructor(props) {
    super(props)
    /**
     * @typedef {Object} OperatorLandingState
     * @property {string} rideDate
     * @property {string} selectedProductLine
     * @property {string} selectedOperatorCustomProduct
     * @property {number} waiverPdfPageCount
     * @property {number} waiverPdfCurrentPage
     * @property {boolean} doFirstAnimation
     * @property {string} queryParams
     * @property {boolean} contentPulled
     * @property {WaiverIntegrationContentDto} metadata
     * @property {boolean} waiverCompleted
     * @property {boolean} productLineChoiceAlreadyMade
     */
    /** @type OperatorLandingState */
    this.state = {
      rideDate: dateTimeFns.format(dateTimeFns.now(), 'yyyy-MM-dd'),
      selectedProductLine: null,
      productLineChoiceAlreadyMade: false,
      selectedOperatorCustomProduct: null,
      waiverPdfPageCount: null,
      waiverPdfCurrentPage: 1,
      doFirstAnimation: false,
      queryParams: null,
      contentPulled: false,
      metadata: null,
      waiverCompleted: false,
      waiverId: null,
      language: 'en-us'
    }
  }

  async getMetadata(lang) {
    const resLang = lang ? (lang.substring(0, 4) === 'en-us' ? 'en-us' : lang.substring(0, 4)) : ''
    this.setState({ language: resLang })
    let response
    const { waiverDataId } = queryParams()
    try {
      response = await api.waivers.getMetadata(this.props.match.params.slug, lang, waiverDataId)
    } catch (err) {
      return
    }

    if (response && response.operatorLandingSlug && response.featureFlags) {
      if (response.featureFlags.includes('UseCustomerWebWaivers')) {
        const customerWebLink = GetExternalUrlsFromDomain(
          'WaiverWeb',
          window.location.origin,
          process.env.REACT_APP_LOCAL_DEV_BACKEND
        ).CustomerWeb
        window.location.replace(`${customerWebLink}/our-outfitters/${response.operatorLandingSlug}/waiver`)
      }
    }

    const pulled = this.state.contentPulled
    const metadata = { ...response }
    if (!metadata.waiverContentItemsV2) metadata.waiverContentItemsV2 = []
    this.setState({ contentPulled: true, metadata, waiverId: uuidv4() }, () => (!pulled ? this.metadataDidMount() : null))
  }

  async componentDidMount() {
    if (!this.state.contentPulled) {
      await this.getMetadata()
    }
    if (!this.state.doFirstAnimation) {
      setTimeout(() => this.setState({ doFirstAnimation: true }), 750)
    }
  }

  isProductLineValid(productLine, customProduct = null) {
    const waiver = this.state.metadata.waiverContentItemsV2.find(
      (x) => x && x.productLine === productLine && x.operatorCustomProduct === customProduct
    )
    return this.isWaiverContentItemValid(waiver)
  }

  /**
   * @param {WaiverContentDto} waiver
   */
  isWaiverContentItemValid(waiver) {
    return waiver && waiver.waiverAgreement
  }

  metadataDidMount() {
    let invalidWaivers = this.state.metadata.waiverContentItemsV2.filter((x) => !this.isWaiverContentItemValid(x))

    if (invalidWaivers.length > 0) {
      Sentry.captureException(
        `invalid metadata for slug: ${this.props.match.params.slug} ${invalidWaivers.map((w) =>
          w ? `${w.productLine} ${w.operatorCustomProduct} ` : 'empty waiver'
        )}`
      )
    }
    const waiverModel = waiverDataModel(
      this.state.metadata,
      queryParams(),
      this.state.metadata.waiverContentItemsV2,
      this.state.metadata.extraAppFeatures
    )
    this.setState({ waiverModel: waiverModel }, () => {
      this.configureAdditionalState()
    })
  }

  configureAdditionalState() {
    const params = queryParams()
    const responseParams = (this.state.metadata && this.state.metadata.waiverData) || {}
    params.country = getCountryCode(responseParams.country || params.country)
    const { waiverDataId } = params
    this.setState({ queryParams: waiverDataId ? responseParams : params })
    const { extraAppFeatures } = this.state.metadata
    const { isEhs } = this.state.waiverModel
    const { isMobileDevice } = this.state.waiverModel

    const rideDate = responseParams.rideDate || params.rideDate
    // Date must be valid and not in past
    const todayDateTime = dateTimeFns.format(dateTimeFns.now(), 'yyyy-MM-dd')
    let rideDateFromQueryParams = validateDateFormat(rideDate, 'yyyy-mm-dd') ? dateFns.format(rideDate, 'yyyy-MM-dd') : false

    let isRideDateFromQueryParams = false
    let isRideDateInPast = false
    if (rideDate) {
      if (!rideDateFromQueryParams) {
        Sentry.captureException('ride date url incorrect:' + rideDate + ' in url ' + window.location)
        rideDateFromQueryParams = todayDateTime
      } else if (
        instantFns.isBefore(new Date(rideDateFromQueryParams).toISOString(), new Date(todayDateTime).toISOString())
      ) {
        isRideDateInPast = true
        isRideDateFromQueryParams = true
      } else {
        isRideDateFromQueryParams = true
      }
    } else {
      rideDateFromQueryParams = todayDateTime
    }

    const showRideDateInPastError = isRideDateInPast && !isEhs
    const offerRideDateChoice = !isRideDateFromQueryParams && !isEhs

    // Product line in query string must be part of the list, and valid
    let productLineChoice = responseParams.productLine || params.productLine || null
    let customProductChoice = responseParams.customProduct || params.customProduct || null
    if (customProductChoice) {
      productLineChoice = 'OperatorCustom'
    }
    if (isEhs) {
      // EHS feature switch forces a particular product line...
      productLineChoice = 'OperatorCustom'
      customProductChoice = 'EHS'
    }
    if (productLineChoice) {
      if (!this.isProductLineValid(productLineChoice, customProductChoice)) {
        Sentry.captureException('product line invalid: ' + productLineChoice + ' in url ' + window.location)
        productLineChoice = null
      }
    }
    const offerProductLinePick = !productLineChoice && !isEhs

    const showRideDate = this.state.waiverModel && this.state.waiverModel.isGuest
    const licenseScanEnabled = extraAppFeatures.includes(signedWaiverWithDLScan) && isMobileDevice
    if (this.state.waiverModel && this.state.waiverModel.cancellationDate) {
      const firstNameCancel = this.state.waiverModel.cancellationAuthorLastName
      const lastNameCancel = this.state.waiverModel.cancellationAuthorFirstName
      const CancellationDate = this.state.waiverModel.cancellationDate
      const cancelMessage = `This Select Reservation was cancelled by ${firstNameCancel} ${lastNameCancel} on ${CancellationDate}`

      this.setState({ cancelMessage: cancelMessage })
    }
    this.setState({
      selectedProductLine: productLineChoice,
      rideDate: rideDateFromQueryParams,
      selectedOperatorCustomProduct: customProductChoice,
      showRideDate,
      offerRideDateChoice,
      showRideDateInPastError,
      offerProductLinePick,
      licenseScanEnabled
    })
  }

  scrollTo(id, pos = 'start', delay = 500) {
    // @ts-ignore
    setTimeout(() => document.getElementById(id).scrollIntoView({ block: pos, behavior: 'smooth' }), delay)
  }

  selectVehicle(selectedProductLine, selectedOperatorCustomProduct) {
    return () => {
      if (!this.state.waiverCompleted) {
        const lastProductLine = this.state.selectedProductLine
        this.setState({ selectedProductLine, selectedOperatorCustomProduct })
        if (lastProductLine === null) {
          this.scrollTo('stepper', 'start', 1000)
        }
      }
    }
  }

  onProductLineUpdate(productLine) {
    waiverDataModelUpdateWithProductLine(this.state.waiverModel, productLine, this.state.metadata.waiverContentItemsV2)
  }

  onComplete() {
    const { t } = this.props
    const { redirectCallbackTitle, redirectCallbackUrl } = this.state.queryParams
    this.setState({ waiverCompleted: true })
    let timerInterval
    if (redirectCallbackTitle && redirectCallbackUrl) {
      swal.fire({
        title: `${redirectCallbackTitle}`,
        html: t('redirectSwalHtml'),
        timer: 5000,
        timerProgressBar: true,
        willOpen: () => {
          swal.showLoading()
          timerInterval = setInterval(() => {
            const content = swal.getContainer()
            if (content) {
              const b = content.querySelector('b')
              if (b) {
                b.textContent = parseInt(swal.getTimerLeft() / 1000)
              }
            }
          }, 100)
        },
        didClose: () => {
          clearInterval(timerInterval)
          const win = window.open(redirectCallbackUrl, '_self')
          win.focus()
          // console.log('Redirecting....')
        }
      })
    }
  }

  waiverHeadlineText() {
    const { t } = this.props
    if (this.state.selectedOperatorCustomProduct === 'EHS') {
      return t('getYourEHSCompleted')
    } else if (this.state.selectedOperatorCustomProduct) {
      return t('getYourWaiverCompleted', {
        name: this.state.selectedOperatorCustomProduct,
        interpolation: { escapeValue: false }
      })
    } else if (this.state.selectedProductLine) {
      const info = productLineInfo[this.state.selectedProductLine] || { friendlyName: this.state.selectedProductLine }
      return t('getYourWaiverCompleted', { name: info.friendlyName, interpolation: { escapeValue: false } })
    } else {
      return t('getYourWaiverCompleted')
    }
  }

  GetAdditionalParticipantCount() {
    const { waiverDataId } = queryParams()
    if (waiverDataId) {
      if (this.state.metadata.waiverData.minors && this.state.metadata.waiverData.minors.length) {
        if (this.state.metadata.waiverData.minors.length > 5) {
          this.state.metadata.waiverData.minors.length = 5
          return 5
        } else {
          return this.state.metadata.waiverData.minors.length
        }
      }
      return 3
    } else {
      switch (this.state.selectedProductLine) {
        case 'ACE':
        case 'SPT':
          return 0
        case 'SLG':
        case 'IND':
        case 'SNO':
          return 1
        case 'ATV':
          return 1
        case 'RGR':
        case 'PON':
        case 'OperatorCustom':
          return 5
        default:
          return 3
      }
    }
  }

  getLocalizedRideDate() {
    const {
      productType = '',
      beginDate = '',
      endDate = '',
      ridingTimeRange = '',
      receivingTimeSlot = '',
      returningTimeSlot = ''
    } = this.state.queryParams
    if (productType) {
      if (productType === 'TimeSlot' && (beginDate || ridingTimeRange)) {
        return (
          <>
            <table className="table-left-width-fit-content">
              <tbody>
                <tr>
                  <td>{beginDate}</td>
                  <td width="15px" />
                  <td>{ridingTimeRange}</td>
                </tr>
              </tbody>
            </table>
          </>
        )
      }
      if (productType === 'Delivery' && beginDate && receivingTimeSlot && endDate && returningTimeSlot) {
        const receivingTimeSlotArray = receivingTimeSlot.split('-')
        const returningTimeSlotArray = returningTimeSlot.split('-')
        return (
          <>
            <table className="table-left-width-fit-content">
              <tbody>
                <tr>
                  <td>Delivery:</td>
                  <td width="15px" />
                  <td>{beginDate}</td>
                  <td width="15px" />
                  <td className="right">{receivingTimeSlotArray[0]}</td>
                  <td width="5px" />
                  <td>-</td>
                  <td className="right">{receivingTimeSlotArray[1]}</td>
                </tr>
                <tr>
                  <td>Return:</td>
                  <td width="15px" />
                  <td>{endDate}</td>
                  <td width="15px" />
                  <td className="right">{returningTimeSlotArray[0]}</td>
                  <td width="5px" />
                  <td>-</td>
                  <td className="right">{returningTimeSlotArray[1]}</td>
                </tr>
              </tbody>
            </table>
          </>
        )
      }
      if (productType === 'Pickup' && beginDate && receivingTimeSlot && endDate && returningTimeSlot) {
        const receivingTimeSlotArray = receivingTimeSlot.split('-')
        const returningTimeSlotArray = returningTimeSlot.split('-')
        return (
          <>
            <table className="table-left-width-fit-content">
              <tbody>
                <tr>
                  <td>Pickup:</td>
                  <td width="15px" />
                  <td>{beginDate}</td>
                  <td width="15px" />
                  <td className="right">{receivingTimeSlotArray[0]}</td>
                  <td width="5px" />
                  <td>-</td>
                  <td className="right">{receivingTimeSlotArray[1]}</td>
                </tr>
                <tr>
                  <td>Return:</td>
                  <td width="15px" />
                  <td>{endDate}</td>
                  <td width="15px" />
                  <td className="right">{returningTimeSlotArray[0]}</td>
                  <td width="5px" />
                  <td>-</td>
                  <td className="right">{returningTimeSlotArray[1]}</td>
                </tr>
              </tbody>
            </table>
          </>
        )
      }
    }
    return new Date(this.state.rideDate).toLocaleDateString(i18n.language, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    })
  }

  heroChild() {
    const { t } = this.props
    return (
      <div style={{ width: '100vw' }}>
        <Zoom>
          <div style={{ textAlign: 'right' }}>
            <div className={classNames('right', 'sm-mh-x5')}>
              <LanguageSwitcher langs={this.state.metadata.additionalLanguages} onLangChange={this.getMetadata.bind(this)} />
            </div>
          </div>
          <Grid container>
            <Grid item md={4} sm={12} xs={12} />
            <Grid item md={4} sm={12} xs={12}>
              <div className="white txt-shadow-1 center" style={{ marginTop: '9rem' }}>
                <Typography variant="h4" className="white mb-1">
                  {t('rideWithUs')}
                </Typography>
                <Typography variant="h2" className="white mb-x5">
                  {this.state.metadata.operatorName}
                </Typography>
              </div>
            </Grid>
            <Grid item md={4} sm={12} xs={12} />
            {this.state.showRideDate && (
              <Typography variant="h4" className="white mb-x5 mt-1 width-inherit">
                <Divider className="bg-white" variant="middle" />
                {this.getLocalizedRideDate()}
              </Typography>
            )}
          </Grid>
        </Zoom>
      </div>
    )
  }

  render() {
    const { t } = this.props
    if (!this.state.contentPulled) {
      return (
        <div style={{ margin: '0 auto', width: '20rem', paddingTop: '5rem', textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )
    }
    // These will eventually come from API
    let waiverContent =
      this.state.metadata.waiverContentItemsV2.find((x) => x && x.productLine === this.state.selectedProductLine) || {}

    return (
      <div className="bg">
        <Hero
          imageSrc={this.state.metadata.operatorHeroImageUrl || process.env.REACT_APP_SPLASH}
          color="#000000"
          isCentered={false}
          minHeight={`calc(${window.innerHeight}px - 6rem)`}
          opacity={0.2}
          parallaxOffset={100}
          transitionDuration={300}
          children={this.heroChild()}
          style={{ width: '100%' }}
        />

        <Paper
          className={classNames('center', 'mh-1', 'sm-mh-x5', 'animate-500ms', {
            'fade-up-start': !this.state.doFirstAnimation,
            'fade-up-end': this.state.doFirstAnimation
          })}
          style={this.state.licenseScanEnabled ? { minHeight: '100vh' } : { minHeight: '100vh', backgroundColor: '#eee' }}
        >
          <Fab className="up-2" onClick={() => this.scrollTo('start-waiver', 'start', 0)}>
            <ArrowDownward />
          </Fab>
          <Typography variant="h4" className="lh-4 up-1" id="start-waiver">
            {!this.state.showRideDateInPastError &&
              this.state.waiverModel &&
              !this.state.waiverModel.cancellationDate &&
              this.waiverHeadlineText()}
          </Typography>
          {!this.state.showRideDateInPastError &&
            this.state.offerRideDateChoice &&
            this.state.waiverModel &&
            !this.state.waiverModel.cancellationDate && (
              <>
                <div className="alt-bg pv-2">
                  <Typography variant="h5" className="lh-2 center">
                    {t('pickYourRideDate')}
                  </Typography>
                  <Flip left>
                    <MyDatePicker
                      value={this.state.rideDate}
                      language={this.state.language}
                      key={this.state.language}
                      onChange={(value) => {
                        if (!this.state.waiverCompleted) {
                          //TODO: remove moment from MyDatePicker component
                          //and return non-moment object or string
                          this.setState({ rideDate: value.local().format('YYYY-MM-DD') })
                          if (this.state.offerProductLinePick) {
                            this.scrollTo('vehicle-picker', 'center', 250)
                          } else {
                            this.scrollTo('stepper', 'start', 100)
                          }
                        }
                      }}
                    />
                  </Flip>
                  <br />
                </div>
              </>
            )}
          {!this.state.showRideDateInPastError &&
            this.state.offerProductLinePick &&
            this.state.waiverModel &&
            !this.state.waiverModel.cancellationDate &&
            this.renderVehiclePicker()}
          {!this.state.showRideDateInPastError && this.state.waiverModel && !this.state.waiverModel.cancellationDate && (
            <>
              <div className="pv-1" />
              <div className={classNames({ 'container ph-1 left': true })}>{this.renderSteps(waiverContent)}</div>
            </>
          )}
          {this.state.showRideDateInPastError && this.state.waiverModel && !this.state.waiverModel.cancellationDate && (
            <Failed type={'rideDatePassed'} />
          )}
          {this.state.waiverModel && this.state.waiverModel.cancellationDate && (
            <Failed message={this.state.cancelMessage} />
          )}
        </Paper>
      </div>
    )
  }

  renderSteps(waiverContent) {
    if (this.state.waiverModel) {
      return (
        <WaiverSteps
          operatorId={this.state.metadata.operatorId}
          waiverData={this.state.metadata.waiverData}
          operatorName={this.state.metadata.operatorName}
          passengerNumber={this.GetAdditionalParticipantCount()}
          waiverContent={waiverContent}
          rideDate={dateFns.format(this.state.rideDate ? this.state.rideDate : dateFns.now(), 'yyyy-MM-dd')}
          productLine={this.state.selectedProductLine}
          operatorCustomProduct={this.state.selectedOperatorCustomProduct}
          queryParams={this.state.queryParams}
          onComplete={this.onComplete.bind(this)}
          onProductLineUpdate={this.onProductLineUpdate.bind(this)}
          waiverModel={this.state.waiverModel}
          extraAppFeatures={this.state.metadata.extraAppFeatures}
          waiverId={this.state.waiverId}
        />
      )
    }
  }

  renderVehiclePicker() {
    const { t } = this.props
    let waiverContents = this.state.metadata.waiverContentItemsV2.filter((item) => this.isWaiverContentItemValid(item))

    const productLineChunks = _.chunk(waiverContents, 2)

    return (
      <div className="pv-2" id="vehicle-picker">
        <Typography variant="h5" className="lh-2 center">
          {t('pickYourVehicle')}
        </Typography>
        {productLineChunks.map((productLines, ix) => (
          <Zoom key={ix}>
            <div>
              <div
                className={classNames('flex container-sm ph-1 sm-ph-x5', { 'container-half': productLines.length === 1 })}
              >
                {productLines.map((pl) => {
                  const info = productLineInfo[pl.productLine] || {}
                  const name = pl.operatorCustomProduct || info.friendlyName || pl.productLine
                  const img = pl.productImage || info.img || '/img/vehicles/default.jpg'
                  return (
                    <ProductLinePicker
                      key={pl.id}
                      label={name}
                      onSelect={this.selectVehicle(pl.productLine, pl.operatorCustomProduct).bind(this)}
                      selected={
                        this.state.selectedProductLine === pl.productLine &&
                        this.state.selectedOperatorCustomProduct === pl.operatorCustomProduct
                      }
                      imageSrc={img}
                    />
                  )
                })}
              </div>
            </div>
          </Zoom>
        ))}
      </div>
    )
  }
}

const productLineInfo = {
  ACE: {
    friendlyName: 'ACE',
    img: '/img/vehicles/ace.jpg'
  },
  ATV: {
    friendlyName: 'ATV',
    img: '/img/vehicles/atv.jpg'
  },
  RGR: {
    friendlyName: 'Ranger',
    img: '/img/vehicles/rgr.jpg'
  },
  RZR: {
    friendlyName: 'RZR',
    img: '/img/vehicles/rzr.jpg'
  },
  SLG: {
    friendlyName: 'Slingshot',
    img: '/img/vehicles/slg.jpg'
  },
  IND: {
    friendlyName: 'Indian',
    img: '/img/vehicles/ind.jpg'
  },
  GEN: {
    friendlyName: 'General/XPEDITION',
    img: '/img/vehicles/gen.jpg'
  },
  SNO: {
    friendlyName: 'Snowmobile',
    img: '/img/vehicles/sno.jpg'
  },
  PON: {
    friendlyName: 'Pontoon',
    img: '/img/vehicles/pon.jpg'
  }
}

OperatorLanding.propTypes = {
  match: PropTypes.any,
  t: PropTypes.any,
  // eslint-disable-next-line react/no-unused-prop-types
  i18n: PropTypes.any
}

export default withTranslation()(OperatorLanding)
